// Fact-card system (EDUCATION-DESIGN.md §3). Cards unlock on the first
// occurrence of a trigger and on every "Why?" tap; unlocked cards live in the
// Encyclopedia and their collection % feeds the certificate.

import raw from '../data/facts.json';
import { bus } from '../core/events';
import type { ChoreId, ConditionId, GameState } from '../core/types';

export type FactCategory =
  | 'feed_water'
  | 'housing'
  | 'health'
  | 'behavior'
  | 'genetics'
  | 'weather'
  | 'neighbors'
  | 'predators';

export interface FactCard {
  id: string;
  category: FactCategory;
  /** `chore:water`, `condition:heat_stress`, `event:first_escape`, `breed:silkie`. */
  trigger: string;
  /** True once the claim is backed by cited research; see docs/fact-sources.md. */
  verified: boolean;
  /** clay-farm-science finding ids backing the claim, when verified. */
  sources?: string;
  /**
   * What kind of source the card rests on. `science` is the default and the only
   * one that can be `verified`; `field_note` is a practice the owner has
   * confirmed and no research has covered (PLAN-04 S6, INC-17).
   */
  sourceKind?: 'science' | 'field_note';
  title: { en: string; es: string };
  body: { en: string; es: string };
  learnMore: { en: string; es: string };
}

export const FACTS: FactCard[] = raw.facts as FactCard[];

const BY_ID = new Map(FACTS.map((f) => [f.id, f]));
const BY_TRIGGER = new Map(FACTS.map((f) => [f.trigger, f]));

export function getFact(id: string): FactCard | undefined {
  return BY_ID.get(id);
}

export function factForTrigger(trigger: string): FactCard | undefined {
  return BY_TRIGGER.get(trigger);
}

export function choreFact(chore: ChoreId): FactCard | undefined {
  return factForTrigger(`chore:${chore}`);
}

export function conditionFact(condition: ConditionId): FactCard | undefined {
  return factForTrigger(`condition:${condition}`);
}

export function factsInCategory(category: FactCategory): FactCard[] {
  return FACTS.filter((f) => f.category === category);
}

export const FACT_CATEGORIES: FactCategory[] = [
  'feed_water',
  'housing',
  'health',
  'behavior',
  'genetics',
  'weather',
  'neighbors',
  'predators',
];

/**
 * Records a card as collected. Returns true only the first time, so callers can
 * use the return value to decide whether to interrupt the player with it.
 */
export function unlockFact(state: GameState, factId: string): boolean {
  if (!BY_ID.has(factId)) {
    console.warn(`[facts] unknown fact id: ${factId}`);
    return false;
  }
  if (state.progression.factsSeen.includes(factId)) return false;
  state.progression.factsSeen.push(factId);
  bus.emit('FACT_UNLOCKED', { factId });
  return true;
}

/** Same, addressed by trigger. Returns the card when it is newly unlocked. */
export function unlockFactForTrigger(state: GameState, trigger: string): FactCard | null {
  const fact = factForTrigger(trigger);
  if (!fact) return null;
  return unlockFact(state, fact.id) ? fact : null;
}

export function collectionPercent(state: GameState): number {
  if (FACTS.length === 0) return 0;
  return Math.round((state.progression.factsSeen.length / FACTS.length) * 100);
}

/** Breed cards are the Breed Scholar requirement, so they get their own list. */
export const BREED_FACT_IDS = FACTS.filter((f) => f.trigger.startsWith('breed:')).map(
  (f) => f.id,
);
